import React, { FC, SyntheticEvent, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import * as ResponseQuery from 'graphql/response.graphql';
import usePermittedSessionPreferences from 'hooks/usePermittedSessionPreferences';
import ICONS from '../Icons';
import PaymentError from '../Error/PaymentError';
import { Response, ResponseInfo } from 'api/data/response/types';
import {
  OnSubmitFunction,
  PaymentErrorProps,
  PaymentErrorType,
  PaymentMethodSource,
  PaymentMethodType,
} from 'api/data/payment/types';
import './style.scss';

interface TerminalPaymentProps {
  onSubmit: OnSubmitFunction;
  pageId?: string;
  response: Response;
  paymentError?: PaymentErrorProps;
  setLoading: (loading: boolean) => void;
  loading: boolean;
  spaceSlug?: string;
}

const POLLING_INTERVAL = 1000;

const TerminalPayment: FC<TerminalPaymentProps> = ({
  onSubmit,
  pageId,
  response,
  paymentError,
  setLoading,
  loading,
  spaceSlug,
}) => {
  const { canAcceptInPersonPayments, connectedPaymentTerminal } = usePermittedSessionPreferences(spaceSlug || '');
  const [errorMessage, setErrorMessage] = useState<string>();

  const { startPolling, stopPolling } = useQuery<ResponseInfo>(ResponseQuery.GetResponsePaymentStatus, {
    variables: { id: response.id },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (response.order.lastPaymentStatus === 'REQUIRES_PAYMENT_METHOD') {
      setLoading(true);
      startPolling(POLLING_INTERVAL);
    } else {
      stopPolling();
      setLoading(false);
    }

    return () => stopPolling();
  }, [response.order.lastPaymentStatus, setLoading, startPolling, stopPolling]);

  useEffect(() => {
    if (loading) {
      setErrorMessage('');
      return;
    }

    if (response.order.lastPaymentStatus === 'FAILED') {
      setErrorMessage('Payment failed. Please try again.');
    }
  }, [response.order.lastPaymentStatus, loading]);

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      await processTerminalPayment();
    } catch (error) {
      setErrorMessage((error as PaymentErrorType).message);
      setLoading(false);
    }
  };

  const processTerminalPayment = async () => {
    await onSubmit({
      response,
      paymentMethodSource: PaymentMethodSource.PAYMENT_TERMINAL,
      paymentMethodType: PaymentMethodType.CARD,
      sessionKey: `${response.id}-payment-terminal`,
    });
  };

  const isSubmitDisabled = loading || !canAcceptInPersonPayments || (!!pageId && response.order.totalCents === 0);

  return (
    <div className="terminal-payment-container">
      {!loading && errorMessage && <PaymentError title={paymentError?.title} errorMessage={errorMessage} />}
      {connectedPaymentTerminal ? (
        <form
          onSubmit={event => void handleSubmit(event)}
          className="payment-method"
          aria-label="payment terminal form">
          <button type="submit" disabled={isSubmitDisabled}>
            {loading ? (
              <span className="row align-center justify-center loading">
                {ICONS['spinner_circle']} Continue on reader
              </span>
            ) : (
              'Charge with card reader'
            )}
          </button>
        </form>
      ) : (
        <PaymentError
          type="warning"
          title="Accept card payments"
          errorMessage="Connect a reader to accept card payments"
        />
      )}
    </div>
  );
};

export default TerminalPayment;
